import { useEffect } from 'react'
import { RouteObject, Navigate, createBrowserRouter, useLocation, useParams, useSearchParams } from 'react-router'
import {
  ErrorPage,
  SignInPage,
  SignUpPage,
  OauthConsentPage,
  // OnboardingWelcomePage,
  // OnboardingPersonalizePage,
  // OnboardingPersonalize2Page,
  // OnboardingMeetCopaPage,
  AtlasPage,
  TeamPage,
  ForgotPasswordPage,
  ForgotPasswordSentPage,
  SetNewPasswordPage,
  ProjectDetailsPage,
  MyProfilePage,
  LegalPage,
  SuperAdminPage,
  LabPage,
  MyProjectsPage,
  SubscriptionPage,
} from 'src/pages'

import DashboardLayout from 'src/enviroment/layouts/dashboard-layout'
import useAuth from 'src/enviroment/hooks/useAuth'
import { toSafeReturnPath } from 'src/enviroment/utils/toSafeReturnPath'
import ProtectedRoute from './protected-route'
import { ROUTES } from 'src/types/routes'
import { UserRole } from 'src/types/user'
import { atlasLoader } from './atlas-loader'

const RootRedirect = () => {
  const { isAuthenticated } = useAuth()
  return isAuthenticated ? <Navigate to="/dashboard/projects" replace /> : <Navigate to="/auth/sign-in" replace />
}

const LogoutPage = () => {
  const { logout } = useAuth()
  const [searchParams] = useSearchParams()
  const returnTo = toSafeReturnPath(searchParams.get('from'))
  // Once per visit: a re-run while the next sign-in is in flight wipes its fresh tokens.
  useEffect(() => {
    logout(returnTo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return <Navigate to="/auth/sign-in" replace state={{ from: returnTo }} />
}

// the section was called Origins until NO-1045; shared links and bookmarks still point there
const LegacyOriginsRedirect = () => {
  const { search, hash } = useLocation()
  const rest = useParams()['*']
  const pathname = rest ? `${ROUTES.LANDSCAPES}/${rest}` : ROUTES.LANDSCAPES
  return <Navigate to={{ pathname, search, hash }} replace />
}

const publicRoutes: RouteObject[] = [
  {
    path: ROUTES.HOME,
    element: <RootRedirect />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.SIGN_IN,
    element: <SignInPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.SIGN_UP,
    element: <SignUpPage />,
    errorElement: <ErrorPage />,
  },
  // Onboarding flow disabled — sign-up now collects workspace info and lands on the atlas
  // {
  //   path: ROUTES.ONBOARDING_WELCOME,
  //   element: <OnboardingWelcomePage />,
  //   errorElement: <ErrorPage />,
  // },
  // {
  //   path: ROUTES.ONBOARDING_PERSONALIZE,
  //   element: <OnboardingPersonalizePage />,
  //   errorElement: <ErrorPage />,
  // },
  // {
  //   path: ROUTES.ONBOARDING_INTERESTS,
  //   element: <OnboardingPersonalize2Page />,
  //   errorElement: <ErrorPage />,
  // },
  // {
  //   path: ROUTES.ONBOARDING_MEET_COPA,
  //   element: <OnboardingMeetCopaPage />,
  //   errorElement: <ErrorPage />,
  // },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <ForgotPasswordPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.FORGOT_PASSWORD_SENT,
    element: <ForgotPasswordSentPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.SET_NEW_PASSWORD,
    element: <SetNewPasswordPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.LOGOUT,
    element: <LogoutPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.LEGAL,
    element: <LegalPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.OAUTH_INTERACTION,
    element: (
      <ProtectedRoute>
        <OauthConsentPage />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },
]

const protectedRoutes: RouteObject[] = [
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      // {
      //   path: 'home',
      //   element: (
      //     <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
      //       <HomePage />
      //     </ProtectedRoute>
      //   ),
      // },
      {
        path: 'projects',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
            <MyProjectsPage mode="tenant" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'landscapes',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
            <MyProjectsPage mode="public" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
            <MyProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'project/:id',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
            <ProjectDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'landscapes/project/:id',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
            <ProjectDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'origins/*',
        element: <LegacyOriginsRedirect />,
      },
      {
        path: 'atlas',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
            <AtlasPage />
          </ProtectedRoute>
        ),
        loader: atlasLoader,
      },
      {
        path: 'team',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <TeamPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'subs',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <SubscriptionPage />
          </ProtectedRoute>
        ),
      },
      // {
      //   path: 'learn',
      //   element: (
      //     <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
      //       <LearnPage />
      //     </ProtectedRoute>
      //   ),
      // },
      // {
      //   path: 'srm',
      //   element: (
      //     <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
      //       <CrmPage />
      //     </ProtectedRoute>
      //   ),
      // },
      // {
      //   path: 'dmrv',
      //   element: (
      //     <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CONTRIBUTOR, UserRole.VIEWER]}>
      //       <DmrvPage />
      //     </ProtectedRoute>
      //   ),
      // },
      {
        path: 'superadmin',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <SuperAdminPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'lab',
        element: (
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <LabPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '',
        element: <Navigate to="projects" replace />,
      },
    ],
  },
]

const routes: RouteObject[] = [...publicRoutes, ...protectedRoutes]

export function createRouter() {
  return createBrowserRouter(routes, {
    future: {
      v7_relativeSplatPath: true,
      v7_startTransition: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  })
}
